/**
 * Provider Registration
 *
 * This file registers all AI provider implementations with the global provider registry.
 * Import this file early in the application lifecycle to ensure providers are available.
 */

import { providerRegistry } from './provider-registry';
import { GeminiProvider } from './gemini-provider';
import { OpenAIProvider } from './openai-provider';
import { FreeTestProvider } from './free-test-provider';
import {
  openRouterProvider,
  chutesProvider,
  manualProvider,
  waveSpeedProvider,
  cometAPIProvider,
} from './all-providers';

/**
 * Register all providers with their metadata
 */
export function registerAllProviders(): void {
  // Register Gemini
  providerRegistry.register({
    id: 'gemini',
    label: 'Google Gemini',
    capabilities: {
      text: true,
      image: true,
      imageEdit: true,
      imageEditIdentityConsistent: true,
      documentSupport: true,
      nativePDF: true,
      internetSearch: false,
    },
    validation: {
      keyFormat: 'Format: AIza... (39 characters)',
      keyPrefix: 'AIza',
      keyLength: { min: 39, max: 39 },
      getKeyUrl: 'https://aistudio.google.com/app/apikey',
      docsUrl: 'https://ai.google.dev/docs',
    },
    display: {
      description:
        "Google's advanced AI model with strong reasoning capabilities and large context windows.",
      notice: 'Supports native PDF processing and image generation with gemini-2.5-flash-image.',
      recommendations: [
        'Use detailed character descriptions for better results',
        'Gemini 2.5 Flash Image Preview provides high-quality image generation',
        'Consider prompt engineering for consistent character features',
      ],
    },
    imageModels: {
      supportsEdit: ['gemini-2.5-flash-image'],
      supportsIdentityConsistentEdit: ['gemini-2.5-flash-image'],
    },
    models: {
      text: ['gemini-2.5-pro', 'gemini-2.5-flash', 'gemini-2.5-flash-lite'],
      image: [
        'gemini-2.5-flash-image',
        'gemini-3-pro-image-preview',
        'imagen-4.0-generate-001',
        'imagen-4.0-ultra-generate-001',
        'imagen-4.0-fast-generate-001',
      ],
      defaults: { text: 'gemini-2.5-flash', image: 'gemini-2.5-flash-image' },
    },
    documentSupport: {
      supportsNativePDF: true,
      maxPDFSize: 10 * 1024 * 1024, // 10MB
      supportedFormats: ['.txt', '.json', '.md', '.pdf'],
      notes: 'PDFs can be sent as inline attachments (up to 10MB).',
    },
    imageDetails: {
      maxResolution: '1152x896',
      maxImagesPerMinute: 10,
      costPerImage: 'Varies by usage (Gemini 2.5 Flash Image Preview)',
      qualityOptions: ['standard'],
      sizeOptions: ['1024x1024', '1152x896', '896x1152'],
    },
    implementation: new GeminiProvider(),
  });

  // Register OpenAI
  providerRegistry.register({
    id: 'openai',
    label: 'OpenAI',
    capabilities: {
      text: true,
      image: true,
      imageEdit: true,
      imageEditIdentityConsistent: true, // gpt-image-1 supports identity consistency
      documentSupport: false,
      nativePDF: false,
      internetSearch: true, // GPT-4.1+, GPT-4o, GPT-5
    },
    validation: {
      keyFormat: 'Format: sk-... (50+ characters)',
      keyPrefix: 'sk-',
      keyLength: { min: 50 },
      getKeyUrl: 'https://platform.openai.com/api-keys',
      docsUrl: 'https://platform.openai.com/docs',
    },
    display: {
      description: "Industry-leading GPT models for various use cases and applications.",
      notice: 'Supports DALL-E 3 and GPT-Image-1 for image generation. Web search available on GPT-4.1+, GPT-4o, and GPT-5.',
      recommendations: [
        'Use detailed character descriptions for better results',
        'DALL-E 3 provides higher quality than DALL-E 2',
        'Consider the cost when generating multiple images',
      ],
    },
    imageModels: {
      supportsEdit: ['gpt-image-1.5', 'gpt-image-1', 'dall-e-3'],
      supportsIdentityConsistentEdit: ['gpt-image-1.5', 'gpt-image-1'],
    },
    models: {
      text: ['gpt-5', 'gpt-5-mini', 'gpt-5-nano', 'gpt-4.1', 'gpt-4o'],
      image: ['gpt-image-1.5', 'gpt-image-1', 'dall-e-3'],
      defaults: { text: 'gpt-5', image: 'gpt-image-1.5' },
    },
    documentSupport: {
      supportsNativePDF: false,
      maxPDFSize: 10 * 1024 * 1024, // 10MB
      supportedFormats: ['.txt', '.json', '.md', '.pdf'],
      notes: 'PDFs are converted to text before processing',
    },
    imageDetails: {
      maxResolution: '1792x1024',
      maxImagesPerMinute: 5,
      costPerImage: '$0.040 per image (DALL-E 3)',
      qualityOptions: ['standard', 'hd'],
      sizeOptions: ['1024x1024', '1024x1792', '1792x1024'],
    },
    implementation: new OpenAIProvider(),
  });

  // Register OpenRouter
  providerRegistry.register({
    id: 'openrouter',
    label: 'OpenRouter',
    capabilities: {
      text: true,
      image: true,
      imageEdit: true,
      imageEditIdentityConsistent: true,
      documentSupport: false,
      nativePDF: false,
      internetSearch: false,
    },
    validation: {
      keyFormat: 'Format: sk-or-... (50+ characters)',
      keyPrefix: 'sk-or-',
      keyLength: { min: 50 },
      getKeyUrl: 'https://openrouter.ai/keys',
      docsUrl: 'https://openrouter.ai/docs',
    },
    display: {
      description:
        'Access to multiple AI models including Claude, GPT, DeepSeek, and more through a unified API.',
      notice: 'Supports many models including free options. Check model pricing on OpenRouter.',
      recommendations: [
        'Use concise prompts to reduce token cost',
        'Ensure model supports image generation (e.g., google/gemini-2.5-flash-image-preview — paid)',
      ],
    },
    imageModels: {
      supportsEdit: ['google/gemini-2.5-flash-image-preview', 'google/gemini-3-pro-image-preview'],
      supportsIdentityConsistentEdit: [
        'google/gemini-2.5-flash-image-preview',
        'google/gemini-3-pro-image-preview',
      ],
    },
    models: {
      text: [
        'deepseek/deepseek-chat-v3.1:free',
        'deepseek/deepseek-r1:free',
        'x-ai/grok-4-fast:free',
        'google/gemini-2.5-pro',
        'openai/gpt-5',
        'google/gemini-2.5-flash',
        'deepseek/deepseek-chat-v3.1',
        'x-ai/grok-4',
      ],
      image: ['google/gemini-2.5-flash-image-preview', 'google/gemini-3-pro-image-preview'],
      defaults: { text: 'deepseek/deepseek-r1:free', image: 'google/gemini-2.5-flash-image-preview' },
    },
    documentSupport: {
      supportsNativePDF: false,
      maxPDFSize: 10 * 1024 * 1024, // 10MB
      supportedFormats: ['.txt', '.json', '.md', '.pdf'],
      notes: 'PDFs are converted to text before processing',
    },
    imageDetails: {
      maxResolution: '1024x1024',
      maxImagesPerMinute: 10,
      costPerImage: 'Depends on selected model',
      qualityOptions: ['standard'],
      sizeOptions: ['1024x1024'],
    },
    implementation: openRouterProvider,
  });

  // Register Chutes
  providerRegistry.register({
    id: 'chutes',
    label: 'Chutes AI',
    capabilities: {
      text: true,
      image: false,
      imageEdit: false,
      imageEditIdentityConsistent: false,
      documentSupport: false,
      nativePDF: false,
      internetSearch: false,
    },
    validation: {
      keyFormat: 'Format: OpenAI-style secret key',
      keyLength: { min: 1 },
      getKeyUrl: 'https://chutes.ai/dashboard',
      docsUrl: 'https://chutes.ai/docs',
    },
    display: {
      description: 'Chutes AI hosts tuned Gemma and DeepSeek models behind an OpenAI-compatible endpoint.',
      notice: 'Configure a provider that supports images (OpenAI, Gemini, or OpenRouter) for character art.',
      recommendations: [
        'Configure a provider that supports images (OpenAI, Gemini, or OpenRouter) for character art',
      ],
    },
    imageModels: {
      supportsEdit: [],
      supportsIdentityConsistentEdit: [],
    },
    models: {
      text: [
        'unsloth/gemma-3-4b-it',
        'unsloth/gemma-3-12b-it',
        'deepseek-ai/DeepSeek-R1',
        'deepseek-ai/DeepSeek-V3-0324',
        'deepseek-ai/DeepSeek-R1-0528-Qwen3-8B',
        'zai-org/GLM-4.5-FP8',
      ],
      image: [],
      defaults: { text: 'unsloth/gemma-3-12b-it' },
    },
    documentSupport: {
      supportsNativePDF: false,
      maxPDFSize: 10 * 1024 * 1024,
      supportedFormats: ['.txt', '.json', '.md', '.pdf'],
      notes: 'Chutes uses an OpenAI-compatible API and ingests PDFs as text.',
    },
    implementation: chutesProvider,
  });

  // Register Manual
  providerRegistry.register({
    id: 'manual',
    label: 'Manual (OpenAI-compatible)',
    capabilities: {
      text: true,
      image: true, // User-declared
      imageEdit: false,
      imageEditIdentityConsistent: false,
      documentSupport: false,
      nativePDF: false,
      internetSearch: false,
    },
    validation: {
      keyFormat: 'Format: varies (non-empty string)',
      keyLength: { min: 1 },
      getKeyUrl: '#',
    },
    display: {
      description: 'Use any OpenAI-compatible API by providing the base URL and model name.',
      notice: 'Image support depends on your custom endpoint configuration.',
    },
    imageModels: {
      supportsEdit: [],
      supportsIdentityConsistentEdit: [],
    },
    models: {
      text: [],
      image: [],
      defaults: {},
    },
    implementation: manualProvider,
  });

  // Register WaveSpeed
  providerRegistry.register({
    id: 'wavespeed',
    label: 'WaveSpeed AI',
    capabilities: {
      text: false,
      image: true,
      imageEdit: true,
      imageEditIdentityConsistent: true,
      documentSupport: false,
      nativePDF: false,
      internetSearch: false,
    },
    validation: {
      keyFormat: 'Format: Bearer token (non-empty string)',
      keyLength: { min: 1 },
      getKeyUrl: 'https://wavespeed.ai',
      docsUrl: 'https://wavespeed.ai/docs',
    },
    display: {
      description: 'Hosted image models like Google Imagen 4, Gemini Image, and Seedream V4.',
      notice: 'Image-only provider. Use square prompts (1:1) for best results.',
      recommendations: [
        'Prefer enable_base64_output for direct embedding in the app',
        'Use square prompts (1:1) for best results with the default UI size',
      ],
    },
    imageModels: {
      supportsEdit: [
        'google/gemini-2.5-flash-image',
        'google/gemini-2.5-flash-image/text-to-image',
        'google/gemini-3-pro-image',
        'bytedance/seedream-v4',
        'bytedance/seedream-v4.5',
        'wavespeed-ai/hunyuan-image-2.1',
        'wavespeed-ai/hunyuan-image-3',
        'alibaba/wan-2.5/text-to-image',
        'wavespeed-ai/qwen-image/text-to-image',
        'wavespeed-ai/qwen-image',
        'wavespeed-ai/flux-2-pro',
        'wavespeed-ai/flux-2-flex',
        'wavespeed-ai/flux-2-dev',
      ],
      supportsIdentityConsistentEdit: [
        'google/gemini-2.5-flash-image',
        'google/gemini-2.5-flash-image/text-to-image',
        'google/gemini-3-pro-image',
        'bytedance/seedream-v4',
        'bytedance/seedream-v4.5',
        'wavespeed-ai/hunyuan-image-3',
        'wavespeed-ai/qwen-image',
        'wavespeed-ai/flux-2-pro',
        'wavespeed-ai/flux-2-flex',
        'wavespeed-ai/flux-2-dev',
      ],
    },
    models: {
      text: [],
      image: [
        'google/gemini-2.5-flash-image/text-to-image',
        'wavespeed-ai/flux-dev',
        'wavespeed-ai/flux-2-pro',
        'wavespeed-ai/flux-2-flex',
        'wavespeed-ai/flux-2-dev',
        'google/imagen4',
        'bytedance/seedream-v4',
        'bytedance/seedream-v4.5',
        'wavespeed-ai/hunyuan-image-2.1',
        'wavespeed-ai/hunyuan-image-3',
        'alibaba/wan-2.5/text-to-image',
        'wavespeed-ai/qwen-image/text-to-image',
        'google/gemini-3-pro-image/text-to-image',
      ],
      defaults: { image: 'bytedance/seedream-v4' },
    },
    imageDetails: {
      maxResolution: '2048x2048',
      maxImagesPerMinute: 10,
      costPerImage: 'Depends on model (Gemini Image, Imagen4, Seedream V4)',
      qualityOptions: ['standard', 'high'],
      sizeOptions: ['1024x1024', '2048x2048'],
    },
    implementation: waveSpeedProvider,
  });

  // Register CometAPI
  providerRegistry.register({
    id: 'cometapi',
    label: 'CometAPI',
    capabilities: {
      text: false,
      image: true,
      imageEdit: true,
      imageEditIdentityConsistent: true,
      documentSupport: false,
      nativePDF: false,
      internetSearch: false,
    },
    validation: {
      keyFormat: 'Format: Bearer token (non-empty string)',
      keyLength: { min: 1 },
      getKeyUrl: 'https://cometapi.com',
      docsUrl: 'https://cometapi.com/docs',
    },
    display: {
      description: 'Image generation via CometAPI. Supports Seedream 4 and other hosted models.',
      notice: 'Image-only provider. Upload a clear reference photo to preserve character identity.',
      recommendations: [
        'Upload a clear reference photo to preserve character identity',
        'Keep instructions focused on lighting or wardrobe changes; the model retains facial features',
        'Allow additional processing time for 4K renders when batching multiple images',
      ],
    },
    imageModels: {
      supportsEdit: [
        'bytedance-seedream-4-0-250828',
        'gemini-2.5-flash-image',
        'gemini-3-pro-image',
        'black-forest-labs/flux-2-pro',
        'black-forest-labs/flux-2-dev',
        'black-forest-labs/flux-2-flex',
      ],
      supportsIdentityConsistentEdit: [
        'bytedance-seedream-4-0-250828',
        'gemini-2.5-flash-image',
        'gemini-3-pro-image',
        'black-forest-labs/flux-2-pro',
        'black-forest-labs/flux-2-dev',
        'black-forest-labs/flux-2-flex',
      ],
    },
    models: {
      text: [],
      image: [
        'bytedance-seedream-4-0-250828',
        'gemini-2.5-flash-image',
        'gemini-3-pro-image',
        'black-forest-labs/flux-2-pro',
        'black-forest-labs/flux-2-dev',
        'black-forest-labs/flux-2-flex',
      ],
      defaults: { image: 'bytedance-seedream-4-0-250828' },
    },
    imageDetails: {
      maxResolution: '4096x4096',
      costPerImage: 'Varies by usage tier (SeeDream 4 identity mode)',
      qualityOptions: ['standard'],
      sizeOptions: ['2048x2048', '4096x4096'],
    },
    implementation: cometAPIProvider,
  });

  // Register Free Test Provider
  providerRegistry.register({
    id: 'free-test-provider',
    label: 'Free Model',
    capabilities: {
      text: true,
      image: false,
      imageEdit: false,
      imageEditIdentityConsistent: false,
      documentSupport: false,
      nativePDF: false,
      internetSearch: false,
    },
    validation: {
      keyFormat: 'No API key required',
      keyLength: { min: 0 },
      getKeyUrl: '#',
    },
    display: {
      description: 'Server-managed evaluation model with limited availability and capabilities for trying the app.',
      notice: 'Free provider supports text generation only. Configure a provider with image support (Gemini, OpenAI, OpenRouter) for character images.',
      recommendations: [
        'Use this provider to generate character profiles and image prompts',
        'Switch to an image-capable provider to generate actual images',
      ],
    },
    imageModels: {
      supportsEdit: [],
      supportsIdentityConsistentEdit: [],
    },
    models: {
      text: ['free-model'],
      image: [],
      defaults: { text: 'free-model' },
    },
    documentSupport: {
      supportsNativePDF: false,
      maxPDFSize: 0,
      supportedFormats: [],
      notes: 'Free provider does not support document processing',
    },
    implementation: new FreeTestProvider(),
  });
}

// Auto-register on import
registerAllProviders();
